import type { ReactNode } from "react";
import Icon from "@/components/icon";
import { cn } from "@/lib/cn";

/**
 * A Telegram-style list row: an optional colored icon tile on the right, a
 * title (+ optional subtitle), and trailing content on the left. Becomes a
 * button when `onClick` is provided. See `docs/telegram-ui.md`.
 */
export default function Row({
  icon,
  iconClassName,
  title,
  subtitle,
  trailing,
  onClick,
  disabled,
  className,
}: {
  icon?: string;
  iconClassName?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  /** Merged over the row's own classes — use it to tweak padding/height. */
  className?: string;
}) {
  const interactive = Boolean(onClick);
  const Tag = interactive ? "button" : "div";

  return (
    <Tag
      {...(interactive ? { type: "button" as const, onClick, disabled } : {})}
      dir="rtl"
      className={cn(
        "flex w-full items-center gap-3 px-4 py-3 text-right",
        interactive && "transition active:bg-content2 disabled:opacity-60",
        className,
      )}
    >
      {icon ? (
        <span
          className={`flex size-8 shrink-0 items-center justify-center rounded-[10px] ${
            iconClassName ?? "bg-default-300"
          }`}
        >
          <Icon name={icon} size={18} className="text-white" />
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="text-sm text-default-900">{title}</div>
        {subtitle ? (
          <p className="mt-0.5 text-xxs text-default-500">{subtitle}</p>
        ) : null}
      </div>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </Tag>
  );
}

/** Inset hairline divider between rows. `inset` aligns it past the icon tile. */
export function RowDivider({ inset }: { inset?: boolean }) {
  return <div className={`h-px bg-content3 ${inset ? "mr-[3.75rem]" : "mx-4"}`} />;
}
